import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import BusinessIcon from '@mui/icons-material/Business';
import { ROUTES } from '../../constants/routes';

const PERKS = [
  'Complete annual full body checkup (114 parameters)',
  '100 free essential tests all year round',
  'Free on-call doctor consultation for all packages',
  'AI health trend analysis + family health locker',
];

const MembershipTeaser = () => {
  const navigate = useNavigate();
  return (
    <Box sx={{ bgcolor: '#fff', py: { xs: 6, md: 10 } }}>
      <Container maxWidth="xl" sx={{ maxWidth: 1100, px: { xs: 2, md: 4 } }}>
        <Box
          sx={{
            bgcolor: 'var(--forest)',
            borderRadius: '24px',
            p: { xs: 3, md: 4.5 },
            position: 'relative',
            overflow: 'hidden',
            maxWidth: 1000,
            mx: 'auto',
          }}
        >
          {/* Glow accents */}
          <Box
            sx={{
              position: 'absolute',
              top: -100,
              right: -100,
              width: 400,
              height: 400,
              background: 'radial-gradient(circle, rgba(61,122,82,0.3) 0%, transparent 70%)',
              borderRadius: '50%',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -60,
              left: 200,
              width: 280,
              height: 280,
              background: 'radial-gradient(circle, rgba(201,125,8,0.1) 0%, transparent 70%)',
              borderRadius: '50%',
              pointerEvents: 'none',
            }}
          />

          {/* 2-col native grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 4, md: 6 },
              alignItems: 'center',
              position: 'relative',
              zIndex: 2,
            }}
          >
            {/* LEFT */}
            <Box>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.625,
                  bgcolor: 'rgba(201,125,8,0.12)',
                  border: '1px solid rgba(201,125,8,0.3)',
                  borderRadius: 100,
                  px: 1.5,
                  py: 0.625,
                  mb: 2.25,
                  lineHeight: 1,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'var(--amber)',
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    lineHeight: 1,
                  }}
                >
                  ✦ TrustLab Membership
                </Box>
              </Box>

              <Typography
                className="serif"
                sx={{
                  fontSize: { xs: 24, sm: 28, md: 32, lg: 36 },
                  fontWeight: 500,
                  fontStyle: 'italic',
                  color: '#fff',
                  lineHeight: 1.15,
                  mb: 2,
                }}
              >
                One membership.
                <br />
                Your health, all year.
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: 13, md: 14 },
                  color: 'rgba(255,255,255,0.5)',
                  maxWidth: 420,
                  mb: 2.75,
                  fontWeight: 300,
                  lineHeight: 1.7,
                }}
              >
                Stop paying per test. Get a complete annual checkup plus 100 free essential tests — for one flat price
                that pays for itself in weeks.
              </Typography>

              {/* Perks */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 3 }}>
                {PERKS.map((p) => (
                  <Box
                    key={p}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      lineHeight: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        bgcolor: 'var(--sage)',
                        color: '#fff',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        fontWeight: 800,
                        flexShrink: 0,
                        lineHeight: 1,
                      }}
                    >
                      ✓
                    </Box>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 13,
                        fontWeight: 400,
                        color: 'rgba(255,255,255,0.75)',
                        lineHeight: 1.2,
                      }}
                    >
                      {p}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Button
                onClick={() => navigate(ROUTES.MEMBERSHIP)}
                sx={{
                  bgcolor: 'var(--amber)',
                  color: '#fff',
                  borderRadius: 100,
                  fontSize: 13,
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 2.75,
                  py: 1.125,
                  '&:hover': { bgcolor: '#C77C1D' },
                }}
              >
                Explore Membership →
              </Button>
            </Box>

            {/* RIGHT */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
              {/* Pricing cards — 2 columns */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1.75,
                }}
              >
                {/* Saver */}
                <Box
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '18px',
                    p: 2.75,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: 'rgba(255,255,255,0.4)',
                      mb: 1.5,
                      lineHeight: 1,
                    }}
                  >
                    TrustLab Membership — Saver
                  </Typography>
                  <Typography
                    className="serif"
                    sx={{
                      fontSize: 36,
                      fontWeight: 600,
                      color: 'var(--sage-l)',
                      lineHeight: 1,
                    }}
                  >
                    ₹999
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: 'rgba(255,255,255,0.4)',
                      mt: 0.625,
                      lineHeight: 1,
                    }}
                  >
                    One-time joining fee
                  </Typography>
                </Box>

                {/* Premium */}
                <Box
                  sx={{
                    bgcolor: 'rgba(201,125,8,0.1)',
                    border: '1.5px solid rgba(201,125,8,0.3)',
                    borderRadius: '18px',
                    p: 2.75,
                    textAlign: 'center',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -10,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: 'var(--amber)',
                      color: '#fff',
                      fontSize: 9,
                      fontWeight: 800,
                      px: 1.25,
                      py: 0.375,
                      borderRadius: 100,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                      lineHeight: 1,
                    }}
                  >
                    Most Popular
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: 'rgba(255,255,255,0.4)',
                      mb: 1.5,
                      lineHeight: 1,
                    }}
                  >
                    TrustLab Membership — Premium
                  </Typography>
                  <Typography
                    className="serif"
                    sx={{
                      fontSize: 36,
                      fontWeight: 600,
                      color: 'var(--amber)',
                      lineHeight: 1,
                    }}
                  >
                    ₹1,999
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: 'rgba(255,255,255,0.4)',
                      mt: 0.625,
                      lineHeight: 1,
                    }}
                  >
                    One-time joining fee
                  </Typography>
                </Box>
              </Box>

              {/* Corporate banner — wraps on phones so the Quote button
                  drops to its own line instead of squeezing the copy. */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexWrap: 'wrap',
                  gap: 1.5,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '14px',
                  p: 1.75,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 18, color: 'var(--sage-l)', display: 'block' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff', mb: 0.25, lineHeight: 1.3 }}>
                    Corporate Plan Available
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      color: 'rgba(255,255,255,0.4)',
                      fontWeight: 300,
                      lineHeight: 1.4,
                    }}
                  >
                    Custom pricing for 10+ employees · HR dashboard included
                  </Typography>
                </Box>
                <Button
                  sx={{
                    flexShrink: 0,
                    borderRadius: 100,
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 1.75,
                    py: 0.625,
                    '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
                  }}
                >
                  Quote →
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default MembershipTeaser;
